import { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, UserPlus } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { useApi } from "../hooks";
import { MetricCard, PageHeader } from "../components/Ui";
import Markdown from "../components/Markdown";
import QuickMenu, { QuickMenuItem } from "../components/QuickMenu";
import { RequestView } from "../components/Workflow";

/**
 * The generator writes a Slack post: an ALL-CAPS emoji greeting and a date line
 * before the content. The page already shows the date, so those lines go. Only
 * those: "🔴 OVERDUE" also starts with an emoji and is the first section.
 */
function stripSlackHeader(content: string): string {
  const lines = content.split("\n");
  let i = 0;
  while (i < lines.length && i < 4) {
    const line = lines[i].trim();
    if (!line || /GOOD (MORNING|AFTERNOON|EVENING)|AGENCY STANDUP/i.test(line) || /^#*\s*📅/u.test(line) || /^\W*\d{1,2}\/\d{1,2}\/\d{2,4}\W*$/.test(line)) i++;
    else break;
  }
  // Trim the end only: the first item's indent is what marks it as a list item.
  return lines.slice(i).join("\n").replace(/\s+$/, "");
}

/** Where each agency status count is worked from. */
const FACT_LINKS: Record<string, string> = {
  "Open approvals": "/approvals",
  // Open anomalies: unreviewed, seen in the last 14 days, one per client and kind (Client health counts the same).
  "Open anomalies": "/health",
  "Overnight anomalies": "/health",
  "Unassigned urgent tasks": "/tasks?owner=none&priority=critical,high",
  "Tasks due today": "/tasks",
  "Overdue tasks": "/tasks?focus=overdue",
};
const EMOJI = /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B50}\u{2B55}\u{FE0F}]/gu;
const sentenceCase = (text: string) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
/** Each task line's text with its task id and section, in order (from GET /api/standup/today). */
type StandupLink = { line: string; id: number; section?: string };
type TaskRow = { id: number; title: string; priority: string | null; section: string };
/** A task line in the converted text: one marker line the page turns into a row. */
const TASK_MARK = "\u0000task ";
/** Agency status counts ("Open approvals: 15") and team workload lines become tiles and a table. */
const FACT_MARK = "\u0000fact ";
const WORK_MARK = "\u0000work ";
type Fact = { label: string; value: number; to?: string };
type Workload = { name: string; open: number; due: number; overdue: number };
const mdText = (text: string) => text.replace(/([\\[\]*_`])/g, "\\$1");
/**
 * The Slack post is plain text: ALL-CAPS section lines, indented "•" items, rules
 * drawn with box characters and text capacity bars. Markdown would run each section
 * into one paragraph, so each line becomes a heading or a list item here instead.
 * A task line becomes a row that opens its task (the priority sits at the row's end).
 */
function slackToMarkdown(text: string, links: StandupLink[] = []): string {
  const out: string[] = [];
  // Two tasks can read the same: each line takes the next id given for its text.
  const ids = new Map<string, StandupLink[]>();
  for (const link of links) ids.set(link.line, [...(ids.get(link.line) || []), link]);
  const priorityId = ids.get("priority")?.[0]?.id;
  for (const raw of text.split("\n")) {
    const link = ids.get(raw.trim())?.shift();
    if (link) {
      const m = /^\s*•\s*\[(CRITICAL|HIGH|MEDIUM|LOW)\]\s*(.*)$/.exec(raw);
      const title = (m ? m[2] : raw.replace(/^\s*•\s*/, "")).replace(EMOJI, "").replace(/\s+/g, " ").trim();
      const row: TaskRow = { id: link.id, title, priority: m ? m[1].toLowerCase() : null, section: link.section || "" };
      out.push(TASK_MARK + JSON.stringify(row));
      continue;
    }
    if (!raw.trim() || /^[\s━─═_=-]+$/.test(raw)) { out.push(""); continue; }
    const indented = /^\s/.test(raw);
    const line = raw
      .replace(/\[[█░▓▒ ]+\]\s*/g, "") // "[███░░░░░░░] 30%" reads as "30%"
      // "[CRITICAL] Title, client" reads "Title, client (critical)": the priority as a short suffix.
      .replace(/\[(CRITICAL|HIGH|MEDIUM|LOW)\]\s*(.*)$/, (_, p: string, rest: string) => `${rest} (${p.toLowerCase()})`)
      .replace(/\s*\b(OVERLOADED|NEAR CAPACITY)\b/g, (_, w: string) => `, ${w.toLowerCase()}`)
      .replace(/\(1 tasks\)/g, "(1 task)")
      .replace(EMOJI, "")
      .replace(/^\s*•\s*/, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!line) continue;
    // "and 4 more in the Work queue" (after the first ten tasks with no owner) opens those tasks.
    const more = /^and (\d+) more in the Work queue$/.exec(line);
    if (more) { out.push("", `[${more[1]} more without an owner in the Work queue](/tasks?owner=none)`, ""); continue; }
    const priority = /^PRIORITY ONE:\s*(.+)$/.exec(line);
    if (priority) { out.push("", "### Priority one", "", priorityId ? `[${mdText(priority[1])}](/tasks?task=${priorityId})` : priority[1]); continue; }
    if (/^For: /.test(line)) { out.push("", line); continue; }
    // "AGENCY STATUS", "DUE TODAY (2 tasks)": a section heading in sentence case.
    const letters = line.replace(/\([^)]*\)/g, "").replace(/[^A-Za-z]/g, "");
    if (!indented && letters.length > 3 && letters === letters.toUpperCase()) {
      out.push("", `### ${sentenceCase(line).replace(/\s+[—–-]\s+/g, ": ")}`, "");
      continue;
    }
    // "Open Approvals: 2" is a tile reading "Open approvals 2" that opens where those items are.
    const count = /^([A-Z][a-z]+(?: [A-Z][a-z]+)+): (\d+)$/.exec(line);
    if (count) { const label = sentenceCase(count[1]); out.push(FACT_MARK + JSON.stringify({ label, value: Number(count[2]), to: FACT_LINKS[label] } satisfies Fact)); continue; }
    // "Ben Poh: 0 open, 0 due today, 0 overdue" is a row in the workload table.
    const load = /^(.+): (\d+) open, (\d+) due today, (\d+) overdue$/.exec(line);
    if (load) { out.push(WORK_MARK + JSON.stringify({ name: load[1], open: Number(load[2]), due: Number(load[3]), overdue: Number(load[4]) } satisfies Workload)); continue; }
    // "Open Approvals: 2" reads "Open approvals: 2", and links to where those items are.
    const fact = line.replace(/^([A-Z][a-z]+(?: [A-Z][a-z]+)+): (\d+)$/, (_, key: string, n: string) => {
      const text = `${sentenceCase(key)}: ${n}`;
      const to = FACT_LINKS[sentenceCase(key)];
      return to && Number(n) > 0 ? `[${text}](${to})` : text;
    });
    out.push(indented ? `- ${fact}` : fact);
  }
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/** The converted text in order: Markdown between runs of task rows. */
type Block = { md: string } | { tasks: TaskRow[] } | { facts: Fact[] } | { workload: Workload[] };
function toBlocks(body: string): Block[] {
  const blocks: Block[] = [];
  for (const line of body.split("\n")) {
    const last = blocks[blocks.length - 1];
    if (line.startsWith(TASK_MARK)) {
      const row = JSON.parse(line.slice(TASK_MARK.length)) as TaskRow;
      if (last && "tasks" in last) last.tasks.push(row); else blocks.push({ tasks: [row] });
    } else if (line.startsWith(FACT_MARK)) {
      const fact = JSON.parse(line.slice(FACT_MARK.length)) as Fact;
      if (last && "facts" in last) last.facts.push(fact); else blocks.push({ facts: [fact] });
    } else if (line.startsWith(WORK_MARK)) {
      const row = JSON.parse(line.slice(WORK_MARK.length)) as Workload;
      if (last && "workload" in last) last.workload.push(row); else blocks.push({ workload: [row] });
    } else if (last && "md" in last) last.md += `\n${line}`;
    else blocks.push({ md: line });
  }
  return blocks.filter((b) => !("md" in b) || b.md.trim());
}

/** Agency status counts as quiet tiles; a count above zero opens where those items are. */
function FactTiles({ facts }: { facts: Fact[] }) {
  return <div className="module-metrics standup-facts">{facts.map((f) => <MetricCard key={f.label} label={f.label} value={f.value} to={f.value > 0 ? f.to : undefined} />)}</div>;
}

/** Team workload as a table with aligned numbers; an overdue count above zero is red. */
function WorkloadTable({ rows }: { rows: Workload[] }) {
  return (
    <div className="table-wrap standup-workload">
      {/* Compact: on phones it stays a table, so the three numbers keep their column names. */}
      <table className="data-table data-table-compact">
        <thead><tr><th>Person</th><th className="num">Open</th><th className="num">Due today</th><th className="num">Overdue</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.name}><td>{r.name}</td><td className="num">{r.open}</td><td className="num">{r.due}</td><td className={r.overdue ? "num standup-overdue" : "num"}>{r.overdue}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

type Person = { id: number; full_name: string };
/**
 * A task line as a row that opens the task. A task that needs an owner gets an
 * Assign chip: pick a person and the row leaves the list on the next refresh.
 */
function TaskRows({ rows, people, onAssigned }: { rows: TaskRow[]; people: Person[] | null; onAssigned: (note: string) => void }) {
  const [busy, setBusy] = useState<number | null>(null);
  async function assign(row: TaskRow, person: Person) {
    setBusy(row.id);
    try {
      await api(`/api/tasks/${row.id}`, { method: "PUT", body: JSON.stringify({ assigned_to: person.id }) });
      onAssigned(`${row.title} assigned to ${person.full_name}.`);
    } catch (error) {
      onAssigned(error instanceof Error ? error.message : "Could not assign the task.");
    } finally { setBusy(null); }
  }
  return (
    <ul className="standup-tasks">
      {rows.map((row) => (
        <li key={`${row.section}-${row.id}`}>
          <Link to={`/tasks?task=${row.id}`}><span className="standup-task-title">{row.title}</span>{row.priority && <span className="standup-task-priority">{sentenceCase(row.priority)}</span>}</Link>
          {row.section === "unowned" && people && people.length > 0 && (
            <QuickMenu label={`Owner for ${row.title}`} align="right" disabled={busy === row.id} trigger={<span className="chip standup-assign"><UserPlus aria-hidden="true" />{busy === row.id ? "Assigning…" : "Assign"}</span>}>
              {(close) => people.map((person) => (
                <QuickMenuItem key={person.id} onClick={() => { close(); void assign(row, person); }}>{person.full_name}</QuickMenuItem>
              ))}
            </QuickMenu>
          )}
        </li>
      ))}
    </ul>
  );
}

/** "Tuesday, Oct 6" from the report's own agency day (YYYY-MM-DD). */
const reportDay = (day: string) =>
  new Date(`${day.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", timeZone: "UTC" });

export default function Standup() {
  const { user } = useAuth();
  const [view, setView] = useState("my_standup");
  const report = useApi<{ report_date: string; content: string; generated_at?: string | null; links?: StandupLink[] }>(
    user
      ? view === "agency"
        ? "/api/standup/agency"
        : `/api/standup/today/${user.id}`
      : null,
  );
  const body = report.data?.content ? slackToMarkdown(stripSlackHeader(report.data.content), report.data.links) : "";
  const clear = !!report.data?.content && body.split("\n").filter((l) => l.trim()).length <= 1;
  const blocks = toBlocks(body);
  const needsOwner = blocks.some((b) => "tasks" in b && b.tasks.some((t) => t.section === "unowned"));
  // Only unowned work listed: say the person's own plate is clear, so "Mine" never reads as a list of their tasks.
  const onlyUnowned = view === "my_standup" && !clear && blocks.length > 0 && blocks.every((b) => !("tasks" in b) || b.tasks.every((t) => t.section === "unowned"));
  const people = useApi<Person[]>(needsOwner && user?.role === "superadmin" ? "/api/tasks/assignees" : null);
  const [notice, setNotice] = useState("");
  return (
    <>
      <PageHeader
        title="Morning standup"
        description={report.data?.report_date ? reportDay(report.data.report_date) : undefined}
        actions={
          <div className="header-controls">
            {user?.role === "superadmin" && (
              <div className="segmented-control compact" aria-label="Standup">
                {[["my_standup", "Mine"], ["agency", "Agency"]].map(([id, label]) => (
                  <button key={id} className={view === id ? "active" : ""} aria-pressed={view === id} onClick={() => setView(id)}>{label}</button>
                ))}
              </div>
            )}
            <button className="button secondary" disabled={report.refreshing} onClick={report.reload}>
              <RefreshCw className={report.refreshing ? "spin" : ""} aria-hidden="true" />
              {report.refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        }
      />
      <RequestView request={report}>
        <section className="panel workflow-panel standup-panel">
          {notice && <p className="standup-meta" role="status">{notice}</p>}
          {clear ? (
            <div className="standup-clear-state">
              <strong>Nothing needs you this morning.</strong>
              <p className="workflow-note">None of your tasks is overdue or due in the next 7 days.</p>
            </div>
          ) : !blocks.length ? (
            <Markdown>No standup content available.</Markdown>
          ) : <>{onlyUnowned && <p className="standup-meta standup-yours">None of your own tasks is overdue or due in the next 7 days.</p>}{blocks.map((block, i) => "md" in block
            ? <Markdown key={i}>{block.md}</Markdown>
            : "facts" in block ? <FactTiles key={i} facts={block.facts} />
            : "workload" in block ? <WorkloadTable key={i} rows={block.workload} />
            : <TaskRows key={i} rows={block.tasks} people={people.data} onAssigned={(note) => { setNotice(note); report.reload(); }} />)}</>}
        </section>
      </RequestView>
    </>
  );
}
